/**
 * Back office → Website (C19, C20): texts of the public pages, offers for the ad campaigns,
 * verified testimonials and the site settings. Everything saved here is online at once.
 */

import {
  ApiError,
  getWebsiteOffersQrRetrieveUrl,
  type Offer,
  type PublicationStatusEnum,
  refreshSession,
  type SiteSection,
  type Testimonial,
  type WebsiteSettings,
  useCatalogPackagesList,
  useWebsiteOffersArchiveCreate,
  useWebsiteOffersCreate,
  useWebsiteOffersList,
  useWebsiteOffersPartialUpdate,
  useWebsiteOffersPublishCreate,
  useWebsiteOffersRetrieve,
  useWebsiteSectionsList,
  useWebsiteSectionsPartialUpdate,
  useWebsiteSettingsList,
  useWebsiteSettingsPartialUpdate,
  useWebsiteTestimonialsCreate,
  useWebsiteTestimonialsList,
  useWebsiteTestimonialsPartialUpdate,
  useWebsiteTestimonialsVerifyCreate,
} from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import {
  Badge,
  type BadgeTone,
  Button,
  GlassCard,
  GlassSheet,
  SelectField,
  Skeleton,
  SoftToggle,
  TextField,
} from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArrowDown,
  ArrowUp,
  Copy,
  Download,
  ExternalLink,
  FileText,
  Plus,
  Printer,
  Rocket,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../components/AppShell";
import { asTexts, localized, type Texts, TranslatedFields } from "../components/TranslatedFields";
import i18next from "../lib/i18n";
import { session } from "../lib/session";
import { fieldErrors, useCanEditSettings, useCurrentRole } from "../lib/settings";
import { dateText, todayInDouala } from "./billing/shared";
import { LaunchCampaignLink } from "./ads/LaunchCampaignLink";
import { BlogTab } from "./website/BlogTab";
import { LocalPagesTab } from "./website/LocalPagesTab";
import { SiteMediaTab } from "./website/SiteMediaTab";

type Tab = "pages" | "blog" | "local" | "offers" | "testimonials" | "media" | "settings";
const TABS: Tab[] = ["pages", "blog", "local", "offers", "testimonials", "media", "settings"];
/** D-138: a trainer only reviews the blog articles of their exams. */
const TRAINER_TABS: Tab[] = ["blog"];
const PAGES = [
  "home",
  "packages",
  "agency",
  "contact",
  "legal",
  "privacy",
  "offer",
  "service-ee",
  "service-ru",
  "service-vv",
  "service-bl",
  "service-tcf",
] as const;
type SitePage = (typeof PAGES)[number];
const LANGS = ["fr", "en"] as const;
type Lang = (typeof LANGS)[number];
const STATUS_TONE: Record<PublicationStatusEnum, BadgeTone> = { draft: "default", published: "success", archived: "danger" };

/** Where the public site lives: same host as the API in production, port 8001 in development. */
export function publicSiteOrigin(origin: string = window.location.origin): string {
  const url = new URL(origin);
  if (url.port === "5173") url.port = "8001";
  return url.origin;
}

/** French address of a page of the public site (Django i18n patterns, /fr/…). */
function pagePath(page: SitePage, serviceSlugs: Record<string, Texts>, offerSlug?: string): string | null {
  const simple: Partial<Record<SitePage, string>> = {
    home: "",
    packages: "formules/",
    agency: "agence/",
    contact: "contact/",
    legal: "mentions-legales/",
    privacy: "confidentialite/",
  };
  if (page in simple) return `/fr/${simple[page] ?? ""}`;
  if (page === "offer") return offerSlug ? `/fr/offres/${offerSlug}/` : null;
  const code = page.slice("service-".length).toUpperCase();
  const slug = serviceSlugs[code]?.fr || code.toLowerCase();
  return `/fr/services/${slug}/`;
}

function useRefreshWebsite() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0] ?? "").startsWith("/api/v1/website/") });
}

/** Every message of a DRF error, for actions whose errors are not tied to one field. */
function errorMessages(error: unknown, fallback: string): string[] {
  if (!error) return [];
  const fields = Object.values(fieldErrors(error));
  if (fields.length) return fields;
  if (error instanceof ApiError && error.detail) return [error.detail];
  return [fallback];
}

function ErrorList({ error }: { error: unknown }) {
  const { t } = useTranslation();
  const messages = errorMessages(error, t("common.error"));
  if (!messages.length) return null;
  return (
    <div role="alert" className="ao-field__error stack">
      {messages.map((m) => (
        <span key={m}>{m}</span>
      ))}
    </div>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="secondary"
      icon={<Copy size={16} aria-hidden="true" />}
      disabled={!value}
      onClick={() =>
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        })
      }
    >
      {copied ? t("website.copied") : label}
    </Button>
  );
}

function numberText(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : String(value);
}

function digits(value: string): string {
  return value.replace(/\D/g, "");
}

function numberOrNull(value: string): number | null {
  return value ? Number(value) : null;
}

/* ------------------------------------------------------------------ Pages */

interface ItemRow {
  key: number;
  fr: { title: string; text: string };
  en: { title: string; text: string };
}

type LangContent = Record<string, unknown>;

function langContent(content: unknown, lang: Lang): LangContent {
  if (!content || typeof content !== "object") return {};
  const value = (content as Record<string, unknown>)[lang];
  return value && typeof value === "object" ? (value as LangContent) : {};
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function itemsOf(content: LangContent): { title: string; text: string }[] {
  const items = Array.isArray(content.items) ? (content.items as unknown[]) : [];
  return items.map((item) =>
    item && typeof item === "object"
      ? { title: str((item as Record<string, unknown>).title), text: str((item as Record<string, unknown>).text) }
      : { title: str(item), text: "" },
  );
}

let rowCounter = 0;
function nextKey(): number {
  rowCounter += 1;
  return rowCounter;
}

function SectionEditor({ section }: { section: SiteSection }) {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const refresh = useRefreshWebsite();
  const save = useWebsiteSectionsPartialUpdate({ mutation: { onSuccess: refresh } });
  const fr = langContent(section.content, "fr");
  const en = langContent(section.content, "en");
  const [title, setTitle] = useState<Texts>({ fr: str(fr.title), en: str(en.title) });
  const [text, setText] = useState<Texts>({ fr: str(fr.text), en: str(en.text) });
  const [cta, setCta] = useState<Texts>({ fr: str(fr.cta), en: str(en.cta) });
  const [visible, setVisible] = useState(section.visible ?? true);
  const [order, setOrder] = useState(numberText(section.order));
  const [items, setItems] = useState<ItemRow[]>(() => {
    const frItems = itemsOf(fr);
    const enItems = itemsOf(en);
    return Array.from({ length: Math.max(frItems.length, enItems.length) }, (_, n) => ({
      key: nextKey(),
      fr: frItems[n] ?? { title: "", text: "" },
      en: enItems[n] ?? { title: "", text: "" },
    }));
  });
  const errors = fieldErrors(save.error);

  const setItem = (key: number, lang: Lang, field: "title" | "text", value: string) =>
    setItems((all) => all.map((row) => (row.key === key ? { ...row, [lang]: { ...row[lang], [field]: value } } : row)));
  const move = (index: number, delta: number) =>
    setItems((all) => {
      const next = [...all];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(index + delta, 0, moved);
      return next;
    });

  const onSave = () => {
    const base = section.content && typeof section.content === "object" ? (section.content as Record<string, unknown>) : {};
    const content: Record<string, unknown> = { ...base };
    for (const lang of LANGS) {
      content[lang] = {
        ...langContent(section.content, lang),
        title: title[lang] ?? "",
        text: text[lang] ?? "",
        cta: cta[lang] ?? "",
        items: items.map((row) => row[lang]),
      };
    }
    save.mutate({ id: section.id, data: { content, visible, order: order ? Number(order) : 0 } });
  };

  return (
    <GlassCard as="section" className="stack site-section-editor" aria-label={section.key}>
      <div className="ao-card__header">
        <div>
          <h2 className="ao-card__title">
            {str(fr.title) || t(`website.kinds.${section.kind}`)}
          </h2>
          <p className="ao-card__subtitle">
            <span className="code-chip">{section.key}</span> {t(`website.kinds.${section.kind}`)}
          </p>
        </div>
        {visible ? <Badge dot tone="success">{t("website.visible")}</Badge> : <Badge dot>{t("website.hidden")}</Badge>}
      </div>
      <fieldset className="plain-fieldset stack" disabled={!canEdit}>
        <div className="form-grid">
          <SoftToggle label={t("website.visible")} checked={visible} onChange={setVisible} disabled={!canEdit} />
          <TextField label={t("website.order")} inputMode="numeric" value={order} error={errors.order} onChange={(e) => setOrder(digits(e.target.value))} />
        </div>
        <TranslatedFields label={t("website.sectionTitle")} value={title} required={false} disabled={!canEdit} onChange={setTitle} />
        <TranslatedFields label={t("website.sectionText")} value={text} multiline required={false} disabled={!canEdit} onChange={setText} />
        <div className="stack">
          <strong>{t("website.items")}</strong>
          {items.length ? null : <p className="muted">{t("website.noItems")}</p>}
          <ol className="plain-list stack">
            {items.map((row, index) => (
              <li key={row.key} className="installment-row stack">
                <div className="row between">
                  <span className="code-chip">{index + 1}</span>
                  <div className="row">
                    <Button variant="ghost" aria-label={t("common.moveUp")} title={t("common.moveUp")} disabled={index === 0} onClick={() => move(index, -1)}>
                      <ArrowUp size={16} aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      aria-label={t("common.moveDown")}
                      title={t("common.moveDown")}
                      disabled={index === items.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDown size={16} aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      aria-label={t("common.remove")}
                      title={t("common.remove")}
                      onClick={() => setItems((all) => all.filter((r) => r.key !== row.key))}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </Button>
                  </div>
                </div>
                <TranslatedFields
                  label={t("website.itemTitle", { n: index + 1 })}
                  value={{ fr: row.fr.title, en: row.en.title }}
                  required={false}
                  disabled={!canEdit}
                  onChange={(v) => {
                    if (v.fr !== row.fr.title) setItem(row.key, "fr", "title", v.fr ?? "");
                    if (v.en !== row.en.title) setItem(row.key, "en", "title", v.en ?? "");
                  }}
                />
                <TranslatedFields
                  label={t("website.itemText", { n: index + 1 })}
                  value={{ fr: row.fr.text, en: row.en.text }}
                  multiline
                  required={false}
                  disabled={!canEdit}
                  onChange={(v) => {
                    if (v.fr !== row.fr.text) setItem(row.key, "fr", "text", v.fr ?? "");
                    if (v.en !== row.en.text) setItem(row.key, "en", "text", v.en ?? "");
                  }}
                />
              </li>
            ))}
          </ol>
          <div className="row">
            <Button
              variant="secondary"
              icon={<Plus size={16} aria-hidden="true" />}
              onClick={() => setItems((all) => [...all, { key: nextKey(), fr: { title: "", text: "" }, en: { title: "", text: "" } }])}
            >
              {t("website.addItem")}
            </Button>
          </div>
        </div>
        <TranslatedFields label={t("website.cta")} value={cta} required={false} disabled={!canEdit} onChange={setCta} />
        <ErrorList error={save.error && !errors.order ? save.error : null} />
        <div className="row">
          <Button disabled={save.isPending} onClick={onSave}>
            {save.isPending ? t("common.loading") : t("common.save")}
          </Button>
          {save.isSuccess ? (
            <span role="status" className="ok">
              {t("website.savedOnline")}
            </span>
          ) : null}
        </div>
      </fieldset>
    </GlassCard>
  );
}

function PagesTab({ settings, offers }: { settings: WebsiteSettings | undefined; offers: Offer[] }) {
  const { t } = useTranslation();
  const [page, setPage] = useState<SitePage>("home");
  const sections = useWebsiteSectionsList({ page });
  const rows = sections.data?.status === 200 ? sections.data.data : undefined;
  const slugs = (settings?.service_slugs ?? {}) as Record<string, Texts>;
  const firstOffer = offers.find((o) => o.status === "published");
  const path = pagePath(page, slugs, firstOffer?.slug);
  const [picked, setPicked] = useState<string | null>(null);
  const current = rows?.find((s) => s.id === picked) ?? rows?.[0];
  return (
    <div className="site-editor">
      <GlassCard as="section" className="stack site-editor__outline" aria-label={t("website.outline")}>
        <div className="stack">
          <SelectField
            label={t("website.page")}
            value={page}
            options={PAGES.map((p) => ({ value: p, label: t(`website.pages.${p}`) }))}
            onChange={(e) => setPage(e.target.value as SitePage)}
          />
        </div>
        {rows === undefined ? <Skeleton style={{ height: 160 }} /> : null}
        {rows && rows.length ? (
          <ol className="plain-list site-outline">
            {rows.map((section, index) => (
              <li key={section.id}>
                <button
                  type="button"
                  className={`site-outline__row${current?.id === section.id ? " site-outline__row--on" : ""}`}
                  aria-current={current?.id === section.id ? "true" : undefined}
                  onClick={() => setPicked(section.id)}
                >
                  <span className="site-outline__n ao-num">{index + 1}</span>
                  <span className="site-outline__text">
                    <strong>{str(langContent(section.content, "fr").title) || t(`website.kinds.${section.kind}`)}</strong>
                    <span className="muted">{t(`website.kinds.${section.kind}`)}</span>
                  </span>
                  <span className={`site-outline__dot${section.visible === false ? "" : " site-outline__dot--on"}`} aria-label={section.visible === false ? t("website.hidden") : t("website.visible")} />
                </button>
              </li>
            ))}
          </ol>
        ) : null}
        {rows && !rows.length ? <p className="muted">{t("website.noSections")}</p> : null}
        <p className="muted site-editor__hint">{t("website.onlineAtOnce")}</p>
        <div className="row">
          {path ? (
            <a className="ao-button ao-button--secondary ao-button--sm" href={`${publicSiteOrigin()}${path}`} target="_blank" rel="noreferrer">
              <ExternalLink size={15} aria-hidden="true" /> {t("website.viewPage")}
            </a>
          ) : (
            <span className="muted">{t("website.offerPageHint")}</span>
          )}
        </div>
      </GlassCard>
      <div className="site-editor__detail">
        {current ? <SectionEditor key={current.id} section={current} /> : null}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Offers */

interface OfferForm {
  code: string;
  slug: string;
  name: Texts;
  headline: Texts;
  benefit: Texts;
  priceMode: "package" | "price";
  package: string;
  price: string;
  strike: string;
  advantages: { key: number; texts: Texts }[];
  conditions: Texts;
  audience: Texts;
  validUntil: string;
  quota: string;
  seatsTaken: string;
}

function toForm(offer: Offer | undefined): OfferForm {
  const advantages = Array.isArray(offer?.advantages) ? (offer.advantages as unknown[]) : [];
  return {
    code: offer?.code ?? "",
    slug: offer?.slug ?? "",
    name: asTexts(offer?.name),
    headline: asTexts(offer?.headline),
    benefit: asTexts(offer?.benefit),
    priceMode: offer?.package ? "package" : "price",
    package: offer?.package ?? "",
    price: numberText(offer?.price_xaf),
    strike: numberText(offer?.strike_price_xaf),
    advantages: advantages.map((a) => ({ key: nextKey(), texts: typeof a === "string" ? { fr: a } : asTexts(a) })),
    conditions: asTexts(offer?.conditions),
    audience: asTexts(offer?.audience),
    validUntil: offer?.valid_until ?? "",
    quota: numberText(offer?.quota),
    seatsTaken: numberText(offer?.seats_taken ?? 0),
  };
}

function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/** While typing: same characters as a slug, but a dash may still be followed by more text. */
function slugInput(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .slice(0, 80);
}

/** The QR endpoint answers image/svg+xml: the generated client parses JSON, so it is fetched here. */
async function fetchQr(id: string): Promise<string> {
  const url = `${import.meta.env.VITE_API_URL ?? ""}${getWebsiteOffersQrRetrieveUrl(id)}`;
  const send = () => {
    const headers = new Headers({ Accept: "image/svg+xml", "Accept-Language": i18next.language, "X-Client": "web" });
    const { accessToken, organization } = session.get();
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    if (organization) headers.set("X-Organization", organization);
    return fetch(url, { credentials: "include", headers });
  };
  let response = await send();
  if (response.status === 401 && (await refreshSession())) response = await send();
  if (!response.ok) throw new Error(`QR failed: ${response.status}`);
  return URL.createObjectURL(new Blob([await response.text()], { type: "image/svg+xml" }));
}

function QrCode({ offer }: { offer: Offer }) {
  const { t } = useTranslation();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let href: string | null = null;
    let live = true;
    fetchQr(offer.id)
      .then((url) => {
        href = url;
        if (live) setSrc(url);
        else URL.revokeObjectURL(url);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
      if (href) URL.revokeObjectURL(href);
    };
  }, [offer.id, offer.code]);
  if (failed) return <p className="ko">{t("website.offers.qrFailed")}</p>;
  if (!src) return <Skeleton style={{ width: 160, height: 160 }} />;
  const download = () => {
    const link = document.createElement("a");
    link.href = src;
    link.download = `qr-${offer.slug}.svg`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
  const print = () => {
    const win = window.open(src, "_blank");
    win?.addEventListener("load", () => win.print());
  };
  return (
    <div className="stack">
      <img src={src} alt={t("website.offers.qrAlt", { code: offer.code })} width={160} height={160} />
      <div className="row">
        <Button variant="secondary" icon={<Download size={16} aria-hidden="true" />} onClick={download}>
          {t("website.offers.downloadQr")}
        </Button>
        <Button variant="secondary" icon={<Printer size={16} aria-hidden="true" />} onClick={print}>
          {t("website.offers.printQr")}
        </Button>
      </div>
    </div>
  );
}

interface AdCopy {
  headline: string;
  text: string;
  language: string;
  source: string;
}

function adCopies(value: unknown): AdCopy[] {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).map((c) => {
    const row = (c && typeof c === "object" ? c : {}) as Record<string, unknown>;
    return { headline: str(row.headline), text: str(row.text), language: str(row.language), source: str(row.source) };
  });
}

function PublishedKit({ id }: { id: string }) {
  const { t } = useTranslation();
  // The flyer PDF is printed after the publication: ask again until its link is there.
  const detail = useWebsiteOffersRetrieve(id, {
    query: { refetchInterval: (q) => (q.state.data?.data.flyer_url ? false : 3000) },
  });
  const offer = detail.data?.status === 200 ? detail.data.data : undefined;
  if (!offer) return <Skeleton style={{ height: 160 }} />;
  const copies = adCopies(offer.ad_copies);
  return (
    <div className="stack">
      <h3>{t("website.offers.kit")}</h3>
      <p className="muted">{t("website.offers.kitIntro")}</p>
      <div className="stack">
        <TextField label={t("website.offers.pageUrl")} value={offer.page_url} readOnly />
        <div className="row">
          <CopyButton value={offer.page_url} label={t("website.copy")} />
          {offer.page_url ? (
            <a className="link" href={offer.page_url} target="_blank" rel="noreferrer">
              <ExternalLink size={16} aria-hidden="true" /> {t("website.viewPage")}
            </a>
          ) : null}
        </div>
        <TextField label={t("website.offers.whatsappUrl")} value={offer.whatsapp_url} hint={offer.whatsapp_url ? undefined : t("website.offers.noWhatsapp")} readOnly />
        <div className="row">
          <CopyButton value={offer.whatsapp_url} label={t("website.copy")} />
        </div>
        <LaunchCampaignLink offerId={offer.id} />
      </div>
      <strong>{t("website.offers.qr")}</strong>
      <QrCode offer={offer} />
      <strong>{t("website.offers.flyer")}</strong>
      {offer.flyer_url ? (
        <a className="link" href={offer.flyer_url} target="_blank" rel="noreferrer">
          <FileText size={16} aria-hidden="true" /> {t("website.offers.openFlyer")}
        </a>
      ) : (
        <p role="status" className="muted">
          {t("website.offers.flyerPrinting")}
        </p>
      )}
      <strong>{t("website.offers.adCopies")}</strong>
      <p className="muted">{t("website.offers.adCopiesIntro")}</p>
      {copies.length ? (
        <ol className="plain-list stack">
          {copies.map((copy, n) => (
            <li key={`${copy.language}-${n}`} className="installment-row stack">
              <div className="row between">
                <div className="row">
                  <Badge>{copy.language.toUpperCase()}</Badge>
                  <Badge tone={copy.source === "ai" ? "offer" : "default"}>
                    {copy.source === "ai" ? t("website.offers.sourceAi") : t("website.offers.sourceTemplate")}
                  </Badge>
                </div>
                <CopyButton value={`${copy.headline}\n\n${copy.text}`} label={t("website.copy")} />
              </div>
              <strong lang={copy.language}>{copy.headline}</strong>
              <p lang={copy.language}>{copy.text}</p>
            </li>
          ))}
        </ol>
      ) : (
        <p className="muted">{t("website.offers.noCopies")}</p>
      )}
    </div>
  );
}

function OfferSheet({ offer, onClose }: { offer: Offer | undefined; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const canEdit = useCanEditSettings();
  const refresh = useRefreshWebsite();
  const packages = useCatalogPackagesList();
  const create = useWebsiteOffersCreate({ mutation: { onSuccess: refresh } });
  const update = useWebsiteOffersPartialUpdate({ mutation: { onSuccess: refresh } });
  const publish = useWebsiteOffersPublishCreate({ mutation: { onSuccess: refresh } });
  const archive = useWebsiteOffersArchiveCreate({ mutation: { onSuccess: refresh } });
  const [current, setCurrent] = useState<Offer | undefined>(offer);
  const [form, setForm] = useState<OfferForm>(() => toForm(offer));
  const [slugTouched, setSlugTouched] = useState(Boolean(offer));
  const set = <K extends keyof OfferForm>(key: K, value: OfferForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const saveError = current ? update.error : create.error;
  const errors = fieldErrors(saveError);
  const published = (packages.data?.status === 200 ? packages.data.data.results : []).filter((p) => p.status === "published");
  const status = current?.status ?? "draft";

  const payload = () => ({
    code: form.code.trim().toUpperCase(),
    slug: form.slug.trim(),
    name: form.name,
    headline: form.headline,
    benefit: form.benefit,
    package: form.priceMode === "package" && form.package ? form.package : null,
    price_xaf: form.priceMode === "price" ? numberOrNull(form.price) : null,
    strike_price_xaf: numberOrNull(form.strike),
    advantages: form.advantages.map((a) => a.texts).filter((a) => Object.values(a).some(Boolean)),
    conditions: form.conditions,
    audience: form.audience,
    valid_until: form.validUntil || null,
    quota: numberOrNull(form.quota),
    seats_taken: form.seatsTaken ? Number(form.seatsTaken) : 0,
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (current) {
      update.mutate({ id: current.id, data: payload() }, { onSuccess: (r) => r.status === 200 && setCurrent(r.data) });
    } else {
      create.mutate({ data: payload() }, { onSuccess: (r) => r.status === 201 && setCurrent(r.data) });
    }
  };
  const onPublish = () => {
    if (!current) return;
    // Save the form first so the publication checks what is on screen.
    update.mutate(
      { id: current.id, data: payload() },
      {
        onSuccess: (r) => {
          if (r.status !== 200) return;
          setCurrent(r.data);
          publish.mutate({ id: current.id }, { onSuccess: (p) => p.status === 200 && setCurrent(p.data) });
        },
      },
    );
  };
  const onArchive = () => {
    if (!current) return;
    archive.mutate({ id: current.id }, { onSuccess: (r) => r.status === 200 && setCurrent(r.data) });
  };
  const pending = create.isPending || update.isPending || publish.isPending || archive.isPending;

  return (
    <GlassSheet open title={current ? current.code : t("website.offers.new")} onClose={onClose}>
      <form className="stack" onSubmit={onSubmit} noValidate>
        {current ? (
          <div className="row">
            <Badge tone={STATUS_TONE[status]}>{t(`website.status.${status}`)}</Badge>
            {current.price_display ? <span>{current.price_display}</span> : null}
          </div>
        ) : null}
        <fieldset className="plain-fieldset stack" disabled={!canEdit}>
          <div className="form-grid">
            <TextField
              label={t("website.offers.code")}
              hint={t("website.offers.codeHint")}
              value={form.code}
              error={errors.code}
              maxLength={40}
              onChange={(e) => {
                const code = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
                setForm((f) => ({ ...f, code, slug: slugTouched ? f.slug : slugify(code) }));
              }}
            />
            <TextField
              label={t("website.offers.slug")}
              hint={t("website.offers.slugHint")}
              value={form.slug}
              error={errors.slug}
              maxLength={80}
              onChange={(e) => {
                setSlugTouched(true);
                set("slug", slugInput(e.target.value));
              }}
            />
          </div>
          <TranslatedFields label={t("website.offers.name")} value={form.name} disabled={!canEdit} error={errors.name} onChange={(v) => set("name", v)} />
          <TranslatedFields label={t("website.offers.headline")} value={form.headline} required={false} disabled={!canEdit} onChange={(v) => set("headline", v)} />
          <TranslatedFields label={t("website.offers.benefit")} value={form.benefit} multiline required={false} disabled={!canEdit} onChange={(v) => set("benefit", v)} />
          <h3>{t("website.offers.price")}</h3>
          <div className="form-grid">
            <SelectField
              label={t("website.offers.priceMode")}
              value={form.priceMode}
              options={[
                { value: "package", label: t("website.offers.fromPackage") },
                { value: "price", label: t("website.offers.ownPrice") },
              ]}
              onChange={(e) => set("priceMode", e.target.value as OfferForm["priceMode"])}
            />
            {form.priceMode === "package" ? (
              <SelectField
                label={t("website.offers.package")}
                hint={t("website.offers.packageHint")}
                value={form.package}
                error={errors.package}
                options={[
                  { value: "", label: packages.isPending ? t("common.loading") : t("website.offers.pickPackage") },
                  ...published.map((p) => ({
                    value: p.id,
                    label: `${p.code} — ${localized(p.name, i18n.language)}${p.total_xaf ? ` · ${formatXaf(p.total_xaf)}` : ""}`,
                  })),
                ]}
                onChange={(e) => set("package", e.target.value)}
              />
            ) : (
              <TextField
                label={t("website.offers.priceXaf")}
                inputMode="numeric"
                value={form.price ? formatXaf(Number(form.price), { suffix: false }) : ""}
                error={errors.price_xaf}
                onChange={(e) => set("price", digits(e.target.value))}
              />
            )}
            <TextField
              label={t("website.offers.strike")}
              hint={t("website.offers.strikeHint")}
              inputMode="numeric"
              value={form.strike ? formatXaf(Number(form.strike), { suffix: false }) : ""}
              error={errors.strike_price_xaf}
              onChange={(e) => set("strike", digits(e.target.value))}
            />
          </div>
          <h3>{t("website.offers.advantages")}</h3>
          <ol className="plain-list stack">
            {form.advantages.map((a, n) => (
              <li key={a.key} className="contract-item">
                <TranslatedFields
                  label={t("website.offers.advantage", { n: n + 1 })}
                  value={a.texts}
                  disabled={!canEdit}
                  onChange={(v) => set("advantages", form.advantages.map((x) => (x.key === a.key ? { ...x, texts: v } : x)))}
                />
                <Button
                  variant="ghost"
                  aria-label={t("common.remove")}
                  title={t("common.remove")}
                  onClick={() => set("advantages", form.advantages.filter((x) => x.key !== a.key))}
                >
                  <Trash2 size={16} aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ol>
          <div className="row">
            <Button
              variant="secondary"
              icon={<Plus size={16} aria-hidden="true" />}
              onClick={() => set("advantages", [...form.advantages, { key: nextKey(), texts: { fr: "", en: "" } }])}
            >
              {t("website.offers.addAdvantage")}
            </Button>
          </div>
          <TranslatedFields label={t("website.offers.conditions")} value={form.conditions} multiline required={false} disabled={!canEdit} onChange={(v) => set("conditions", v)} />
          <TranslatedFields label={t("website.offers.audience")} value={form.audience} required={false} disabled={!canEdit} onChange={(v) => set("audience", v)} />
          <div className="form-grid">
            <TextField
              label={t("website.offers.validUntil")}
              type="date"
              min={todayInDouala()}
              value={form.validUntil}
              error={errors.valid_until}
              onChange={(e) => set("validUntil", e.target.value)}
            />
            <TextField
              label={t("website.offers.quota")}
              hint={t("website.offers.quotaHint")}
              inputMode="numeric"
              value={form.quota}
              error={errors.quota}
              onChange={(e) => set("quota", digits(e.target.value))}
            />
            <TextField
              label={t("website.offers.seatsTaken")}
              inputMode="numeric"
              value={form.seatsTaken}
              error={errors.seats_taken}
              onChange={(e) => set("seatsTaken", digits(e.target.value))}
            />
          </div>
          {saveError && !Object.keys(errors).some((k) => k !== "detail") ? <ErrorList error={saveError} /> : null}
          <ErrorList error={publish.error ?? archive.error} />
          <div className="row">
            <Button type="submit" disabled={pending}>
              {pending ? t("common.loading") : current ? t("common.save") : t("website.offers.create")}
            </Button>
            {current && status !== "archived" ? (
              <Button variant="secondary" icon={<Rocket size={16} aria-hidden="true" />} disabled={pending} onClick={onPublish}>
                {status === "published" ? t("website.offers.republish") : t("website.offers.publish")}
              </Button>
            ) : null}
            {current && status !== "archived" ? (
              <Button variant="ghost" icon={<Archive size={16} aria-hidden="true" />} disabled={pending} onClick={onArchive}>
                {t("website.offers.archive")}
              </Button>
            ) : null}
            {update.isSuccess || create.isSuccess ? (
              <span role="status" className="ok">
                {t("common.saved")}
              </span>
            ) : null}
          </div>
          {current && status === "draft" ? <p className="muted">{t("website.offers.publishHint")}</p> : null}
        </fieldset>
      </form>
      {current && status === "published" ? <PublishedKit key={current.published_at ?? current.id} id={current.id} /> : null}
    </GlassSheet>
  );
}

function OffersTab({ offers, loading }: { offers: Offer[]; loading: boolean }) {
  const { t, i18n } = useTranslation();
  const canEdit = useCanEditSettings();
  const [editing, setEditing] = useState<Offer | "new" | null>(null);
  return (
    <div className="stack">
      <GlassCard as="section" className="stack">
        <div className="section-title">
          <div>
            <h2>{t("website.offers.title")}</h2>
            <p className="muted">{t("website.offers.intro")}</p>
          </div>
          {canEdit ? (
            <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setEditing("new")}>
              {t("website.offers.new")}
            </Button>
          ) : null}
        </div>
        {loading ? (
          <Skeleton style={{ height: 160 }} />
        ) : offers.length ? (
          <table className="table">
            <thead>
              <tr>
                <th>{t("website.offers.code")}</th>
                <th>{t("website.offers.name")}</th>
                <th>{t("website.offers.price")}</th>
                <th>{t("website.offers.validUntil")}</th>
                <th>{t("website.offers.seatsLeft")}</th>
                <th>{t("website.offers.statusLabel")}</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => (
                <tr key={o.id}>
                  <td>
                    <button type="button" className="link" onClick={() => setEditing(o)}>
                      {o.code}
                    </button>
                  </td>
                  <td>{localized(o.name, i18n.language)}</td>
                  <td>{o.price_display || "—"}</td>
                  <td>{dateText(o.valid_until, i18n.language)}</td>
                  <td>{o.seats_left === null ? "—" : o.seats_left}</td>
                  <td>
                    <Badge tone={STATUS_TONE[o.status]}>{t(`website.status.${o.status}`)}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">{t("website.offers.empty")}</p>
        )}
      </GlassCard>
      {editing ? <OfferSheet key={editing === "new" ? "new" : editing.id} offer={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

/* ----------------------------------------------------------- Testimonials */

function NewTestimonialSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshWebsite();
  const create = useWebsiteTestimonialsCreate({ mutation: { onSuccess: refresh } });
  const [name, setName] = useState("");
  const [text, setText] = useState<Texts>({ fr: "", en: "" });
  const [service, setService] = useState("");
  const [consent, setConsent] = useState("");
  const errors = fieldErrors(create.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        data: {
          name: name.trim(),
          text,
          service: service.trim(),
          // A date typed in Douala (UTC+1), kept at noon so it never shifts by a day.
          consent_at: consent ? `${consent}T12:00:00+01:00` : null,
        },
      },
      { onSuccess: (r) => r.status === 201 && onClose() },
    );
  };
  return (
    <GlassSheet open title={t("website.testimonials.new")} onClose={onClose}>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <TextField label={t("website.testimonials.name")} hint={t("website.testimonials.nameHint")} value={name} maxLength={80} error={errors.name} onChange={(e) => setName(e.target.value)} />
        <TranslatedFields label={t("website.testimonials.text")} value={text} multiline error={errors.text} onChange={setText} />
        <TextField label={t("website.testimonials.service")} value={service} maxLength={80} error={errors.service} onChange={(e) => setService(e.target.value)} />
        <TextField
          label={t("website.testimonials.consent")}
          hint={t("website.testimonials.consentHint")}
          type="date"
          max={todayInDouala()}
          value={consent}
          error={errors.consent_at}
          onChange={(e) => setConsent(e.target.value)}
        />
        {errors.detail || errors.non_field_errors ? (
          <p role="alert" className="ao-field__error">
            {errors.detail ?? errors.non_field_errors}
          </p>
        ) : null}
        <div className="row">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? t("common.loading") : t("website.testimonials.create")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </GlassSheet>
  );
}

function TestimonialRow({ row }: { row: Testimonial }) {
  const { t, i18n } = useTranslation();
  const canEdit = useCanEditSettings();
  const refresh = useRefreshWebsite();
  const update = useWebsiteTestimonialsPartialUpdate({ mutation: { onSuccess: refresh } });
  const verify = useWebsiteTestimonialsVerifyCreate({ mutation: { onSuccess: refresh } });
  return (
    <tr>
      <td>
        <strong>{row.name}</strong>
        <p className="muted">{localized(row.text, i18n.language)}</p>
        <ErrorList error={update.error ?? verify.error} />
      </td>
      <td>{row.service || "—"}</td>
      <td>{row.consent_at ? dateText(row.consent_at, i18n.language) : <Badge tone="danger">{t("website.testimonials.noConsent")}</Badge>}</td>
      <td>
        {row.verified_by_name ? (
          <Badge tone="success">{t("website.testimonials.verifiedBy", { name: row.verified_by_name })}</Badge>
        ) : (
          <Button
            variant="secondary"
            icon={<ShieldCheck size={16} aria-hidden="true" />}
            disabled={!canEdit || verify.isPending}
            onClick={() => verify.mutate({ id: row.id })}
          >
            {t("website.testimonials.verify")}
          </Button>
        )}
      </td>
      <td>
        <SoftToggle
          label={t("website.visible")}
          checked={Boolean(row.visible)}
          disabled={!canEdit || update.isPending}
          onChange={(visible) => update.mutate({ id: row.id, data: { visible } })}
        />
      </td>
    </tr>
  );
}

function TestimonialsTab() {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const list = useWebsiteTestimonialsList();
  const [creating, setCreating] = useState(false);
  const rows = list.data?.status === 200 ? list.data.data.results : undefined;
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("website.testimonials.title")}</h2>
          <p className="muted">{t("website.testimonials.intro")}</p>
        </div>
        {canEdit ? (
          <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => setCreating(true)}>
            {t("website.testimonials.new")}
          </Button>
        ) : null}
      </div>
      <p role="note" className="notice-warning installment-row">
        {t("website.testimonials.rule")}
      </p>
      {rows === undefined ? (
        <Skeleton style={{ height: 160 }} />
      ) : rows.length ? (
        <table className="table">
          <thead>
            <tr>
              <th>{t("website.testimonials.name")}</th>
              <th>{t("website.testimonials.service")}</th>
              <th>{t("website.testimonials.consentShort")}</th>
              <th>{t("website.testimonials.verification")}</th>
              <th>{t("website.visible")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <TestimonialRow key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">{t("website.testimonials.empty")}</p>
      )}
      {creating ? <NewTestimonialSheet onClose={() => setCreating(false)} /> : null}
    </GlassCard>
  );
}

/* --------------------------------------------------------------- Settings */

interface FigureRow {
  key: number;
  value: string;
  label: Texts;
}

function SettingsTab({ row }: { row: WebsiteSettings }) {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const refresh = useRefreshWebsite();
  const save = useWebsiteSettingsPartialUpdate({ mutation: { onSuccess: refresh } });
  const [published, setPublished] = useState(row.published ?? true);
  const [figures, setFigures] = useState<FigureRow[]>(() =>
    (Array.isArray(row.figures) ? (row.figures as unknown[]) : []).map((f) => {
      const item = (f && typeof f === "object" ? f : {}) as Record<string, unknown>;
      return { key: nextKey(), value: typeof item.value === "number" ? String(item.value) : "", label: asTexts(item.label) };
    }),
  );
  const [hours, setHours] = useState<Texts>(asTexts(row.opening_hours));
  const [mapUrl, setMapUrl] = useState(String(row.map_url ?? ""));
  const [slugs, setSlugs] = useState<Record<string, Texts>>(() =>
    Object.fromEntries(Object.entries((row.service_slugs ?? {}) as Record<string, unknown>).map(([code, v]) => [code, asTexts(v)])),
  );
  const [newCode, setNewCode] = useState("");
  const [consent, setConsent] = useState<Texts>(asTexts(row.consent_text));
  const [partner, setPartner] = useState<Texts>(asTexts(row.regulated_partner));
  const errors = fieldErrors(save.error);

  const onSave = () =>
    save.mutate({
      id: row.id,
      data: {
        published,
        figures: figures.filter((f) => f.value !== "").map((f) => ({ value: Number(f.value), label: f.label })),
        opening_hours: hours,
        map_url: mapUrl.trim(),
        service_slugs: slugs,
        consent_text: consent,
        regulated_partner: partner,
      },
    });

  return (
    <GlassCard as="section" className="stack">
      <fieldset className="plain-fieldset stack" disabled={!canEdit}>
        <div className="section-title">
          <div>
            <h2>{t("website.settings.online")}</h2>
            <p className="muted">{t("website.settings.onlineHint")}</p>
          </div>
          <Badge tone={published ? "success" : "danger"}>{published ? t("website.settings.isOnline") : t("website.settings.isOffline")}</Badge>
        </div>
        <SoftToggle label={t("website.settings.publish")} checked={published} disabled={!canEdit} onChange={setPublished} />

        <h3>{t("website.settings.figures")}</h3>
        <p role="note" className="notice-warning installment-row">
          {t("website.settings.figuresWarning")}
        </p>
        {errors.figures ? (
          <p role="alert" className="ao-field__error">
            {errors.figures}
          </p>
        ) : null}
        <ol className="plain-list stack">
          {figures.map((f, n) => (
            <li key={f.key} className="installment-row stack">
              <div className="row between">
                <TextField
                  label={t("website.settings.figureValue", { n: n + 1 })}
                  inputMode="numeric"
                  value={f.value}
                  onChange={(e) => setFigures((all) => all.map((x) => (x.key === f.key ? { ...x, value: digits(e.target.value) } : x)))}
                />
                <Button variant="ghost" aria-label={t("common.remove")} title={t("common.remove")} onClick={() => setFigures((all) => all.filter((x) => x.key !== f.key))}>
                  <Trash2 size={16} aria-hidden="true" />
                </Button>
              </div>
              <TranslatedFields
                label={t("website.settings.figureLabel")}
                value={f.label}
                disabled={!canEdit}
                onChange={(label) => setFigures((all) => all.map((x) => (x.key === f.key ? { ...x, label } : x)))}
              />
            </li>
          ))}
        </ol>
        <div className="row">
          <Button variant="secondary" icon={<Plus size={16} aria-hidden="true" />} onClick={() => setFigures((all) => [...all, { key: nextKey(), value: "", label: { fr: "", en: "" } }])}>
            {t("website.settings.addFigure")}
          </Button>
        </div>

        <h3>{t("website.settings.contact")}</h3>
        <TranslatedFields label={t("website.settings.hours")} value={hours} multiline required={false} disabled={!canEdit} onChange={setHours} />
        <TextField label={t("website.settings.mapUrl")} type="url" value={mapUrl} error={errors.map_url} onChange={(e) => setMapUrl(e.target.value)} />

        <h3>{t("website.settings.slugs")}</h3>
        <p className="muted">{t("website.settings.slugsHint")}</p>
        {errors.service_slugs ? (
          <p role="alert" className="ao-field__error">
            {errors.service_slugs}
          </p>
        ) : null}
        {Object.entries(slugs).map(([code, value]) => (
          <div key={code} className="installment-row stack">
            <div className="row between">
              <span className="code-chip">{code}</span>
              <Button
                variant="ghost"
                aria-label={t("common.remove")}
                title={t("common.remove")}
                onClick={() => setSlugs((all) => Object.fromEntries(Object.entries(all).filter(([c]) => c !== code)))}
              >
                <Trash2 size={16} aria-hidden="true" />
              </Button>
            </div>
            <TranslatedFields
              label={t("website.settings.slug", { code })}
              value={value}
              disabled={!canEdit}
              onChange={(v) => setSlugs((all) => ({ ...all, [code]: Object.fromEntries(Object.entries(v).map(([l, s]) => [l, slugInput(s)])) }))}
            />
          </div>
        ))}
        <div className="row">
          <TextField label={t("website.settings.serviceCode")} value={newCode} maxLength={20} onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} />
          <Button
            variant="secondary"
            icon={<Plus size={16} aria-hidden="true" />}
            disabled={!newCode || newCode in slugs}
            onClick={() => {
              setSlugs((all) => ({ ...all, [newCode]: { fr: newCode.toLowerCase(), en: newCode.toLowerCase() } }));
              setNewCode("");
            }}
          >
            {t("website.settings.addSlug")}
          </Button>
        </div>

        <h3>{t("website.settings.legal")}</h3>
        <TranslatedFields label={t("website.settings.consent")} value={consent} multiline required={false} disabled={!canEdit} onChange={setConsent} />
        <TranslatedFields label={t("website.settings.partner")} value={partner} multiline required={false} disabled={!canEdit} onChange={setPartner} />

        {save.error && !errors.figures && !errors.map_url && !errors.service_slugs ? <ErrorList error={save.error} /> : null}
        <div className="row">
          <Button disabled={save.isPending} onClick={onSave}>
            {save.isPending ? t("common.loading") : t("common.save")}
          </Button>
          {save.isSuccess ? (
            <span role="status" className="ok">
              {t("website.savedOnline")}
            </span>
          ) : null}
        </div>
      </fieldset>
    </GlassCard>
  );
}

/* ------------------------------------------------------------------- Page */

export function WebsitePage() {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const trainer = useCurrentRole("website") === "trainer";
  const tabs = trainer ? TRAINER_TABS : TABS;
  const [chosen, setTab] = useState<Tab>("pages");
  const tab = tabs.includes(chosen) ? chosen : tabs[0];
  const settingsList = useWebsiteSettingsList(undefined, { query: { enabled: !trainer } });
  const offersList = useWebsiteOffersList(undefined, { query: { enabled: !trainer } });
  const settings = settingsList.data?.status === 200 ? settingsList.data.data.results[0] : undefined;
  const offers = offersList.data?.status === 200 ? offersList.data.data.results : [];
  return (
    <Page
      title={t("website.title")}
      description={t("website.intro")}
      actions={
        <a className="link" href={`${publicSiteOrigin()}/fr/`} target="_blank" rel="noreferrer">
          <ExternalLink size={16} aria-hidden="true" /> {t("website.viewSite")}
        </a>
      }
    >
      {canEdit || trainer ? null : (
        <p role="note" className="muted">
          {t("common.readOnly")}
        </p>
      )}
      <div className="tabs" role="tablist" aria-label={t("website.sections")}>
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`website-tab-${key}`}
            aria-selected={tab === key}
            aria-controls={`website-panel-${key}`}
            className={`tabs__tab${tab === key ? " active" : ""}`}
            onClick={() => setTab(key)}
          >
            {key === "media" ? t("websiteMedia.tab") : t(`website.tab.${key}`)}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`website-panel-${tab}`} aria-labelledby={`website-tab-${tab}`}>
        {tab === "pages" ? <PagesTab settings={settings} offers={offers} /> : null}
        {tab === "blog" ? <BlogTab /> : null}
        {tab === "local" ? <LocalPagesTab /> : null}
        {tab === "offers" ? <OffersTab offers={offers} loading={offersList.isPending} /> : null}
        {tab === "testimonials" ? <TestimonialsTab /> : null}
        {tab === "media" ? <SiteMediaTab /> : null}
        {tab === "settings" ? settings ? <SettingsTab key={settings.id} row={settings} /> : <Skeleton style={{ height: 240 }} /> : null}
      </div>
    </Page>
  );
}
